/**
 * RBAC Permission Logic - Shared between Backend and Frontend
 *
 * This module contains PURE permission checking logic.
 * No dependencies on Cloudflare Workers, Hono, React, or any framework.
 *
 * Architecture: Declarative Permission Matrix
 * - PERMISSION_MATRIX defines rules for each resource/action combination
 * - checkResource() evaluates rules in a consistent order
 * - No imperative check* functions — single source of truth
 *
 * Usage:
 * - Backend: Validates actions before executing them (security)
 * - Frontend: Shows/hides UI elements (UX)
 */

import type {
  AuthContext,
  Action,
  ResourceContext,
  PermissionCheckResult,
  PermissionRule,
  PermissionMatrix,
  GlobalRole,
  ProjectRole,
} from './types';

// ============================================================================
// Permission Check Helpers
// ============================================================================

const allow = (): PermissionCheckResult => ({ allowed: true });
const deny = (reason: string): PermissionCheckResult => ({ allowed: false, reason });
/**
 * Denials that are not a role decision carry `denialKind`, so a logger can tell
 * a route that described its resource wrongly (`validation`) and an attempt to
 * cross the organization boundary (`scope`) from a plain refusal of the role.
 */
const denyInvalid = (reason: string): PermissionCheckResult => ({
  allowed: false,
  reason,
  denialKind: 'validation',
});
const denyScope = (reason: string): PermissionCheckResult => ({
  allowed: false,
  reason,
  denialKind: 'scope',
});

/** Check if auth has one of the specified org-level roles */
function hasRole(auth: AuthContext, roles: GlobalRole[]): boolean {
  return auth.globalRole !== null && roles.includes(auth.globalRole);
}

/** Check if auth has exactly this role */
function isRole(auth: AuthContext, role: GlobalRole): boolean {
  return auth.globalRole === role;
}

/**
 * `user.manage_access` (E1.5 PR 5, #1336): the org roles that may assign or
 * revoke a narrow analytics grant. Exported — with {@link rolesManagingAccessOf}
 * — because the grant write rechecks the same authority INSIDE its SQL
 * (`assignerAuthoritySql`, `@aica/database`): one list, so the route's verdict
 * and the write's condition cannot drift apart.
 */
export const MANAGE_ACCESS_ROLES = [
  'client',
  'admin',
] as const satisfies readonly GlobalRole[];

/**
 * The roles that may manage the access of a person whose org role is
 * `targetRole`: narrowing a client is demoting one, so — as for the role
 * (#877) — only a client touches a client.
 */
export function rolesManagingAccessOf(targetRole: GlobalRole): readonly GlobalRole[] {
  return targetRole === 'client' ? ['client'] : MANAGE_ACCESS_ROLES;
}

/** Deny if accessing another org's resources */
function denyIfOrgMismatch(
  auth: AuthContext,
  orgId: string | undefined
): PermissionCheckResult | null {
  if (orgId && orgId !== auth.orgId) {
    return denyScope('Can only access resources in your own org');
  }
  return null;
}

/** Check if user has access via project assignment */
function hasProjectAccess(
  auth: AuthContext,
  projectId: string | undefined,
  requiredRole?: ProjectRole
): boolean {
  if (!projectId) return false;
  const role = auth.projectRoles.get(projectId);
  if (!role) return false;
  if (requiredRole && role !== requiredRole) return false;
  return true;
}

/**
 * Org-structure layer: the route already confirmed the resource is inside
 * the caller's visibility boundary.
 *
 * Only unlocks project-scoped roles — client/admin already pass through
 * `roles` earlier, and an unrecognized role must not gain access from the
 * flag alone. `null` = "keep evaluating the normal rules".
 *
 * The org check is a strict `!==`, not a falsy-guarded `orgId && orgId !==`:
 * `record.read`/`record.rate`/`analysis.read` validate on `projectId`, not
 * `orgId` (see `validationRules.record` below), so a caller can reach this
 * function with `orgId` entirely omitted. A falsy guard would treat "no
 * orgId" as "no boundary to check" and let the flag through unchecked —
 * exactly when the org boundary is what's missing. Strict equality makes an
 * omitted orgId a denial, matching the fail-closed contract on the flag
 * itself.
 */
const hierarchyAllows = (
  auth: AuthContext,
  _action: Action,
  r: ResourceContext
): PermissionCheckResult | null => {
  if (r.visibleViaHierarchy !== true) return null;
  if (r.orgId !== auth.orgId) return null; // no orgId, or a mismatched one — don't cross the org boundary
  return hasRole(auth, ['supervisor', 'agent']) ? allow() : null;
};

// ============================================================================
// Validation Rules (Table-Driven)
// ============================================================================

type ValidationRule = {
  actions: Action[];
  requires: 'id' | 'orgId' | 'projectId' | 'targetUserOrgId';
  error: string;
};

const validationRules: Partial<Record<ResourceContext['type'], ValidationRule[]>> = {
  project: [
    {
      actions: ['update', 'delete', 'archive'],
      requires: 'id',
      error: 'project id required',
    },
    {
      actions: ['create', 'update', 'delete'],
      requires: 'orgId',
      error: 'orgId required',
    },
  ],
  record: [
    {
      actions: ['create', 'read', 'delete', 'update', 'upload', 'rate'],
      requires: 'projectId',
      error: 'projectId required',
    },
    // `list` requires orgId, not projectId — swapped, not dropped, for the
    // same reason as `analysis.list` below (audit H7).
    { actions: ['list'], requires: 'orgId', error: 'orgId required' },
  ],
  // `list` requires orgId, not projectId. Its only caller is the org-wide BI
  // digest (`analytics.ts`), which spans every project and so has no single
  // projectId; demanding one denied the route to EVERYONE — validation runs
  // before the owner short-circuit, so even the platform owner got 403, and
  // since nothing calls the route the failure never surfaced.
  //
  // Swapping the requirement rather than dropping it keeps the boundary
  // declarative. With no requirement at all, a caller that forgot orgId would
  // leave `requiresOrgMatch` inert and hand client/admin an unbounded allow —
  // the same omission that audit finding H7 traced on `record.read`.
  analysis: [
    { actions: ['read'], requires: 'projectId', error: 'projectId required' },
    { actions: ['list'], requires: 'orgId', error: 'orgId required' },
  ],
  alert: [
    { actions: ['read', 'list'], requires: 'projectId', error: 'projectId required' },
  ],
  user: [
    {
      actions: ['list', 'read', 'create', 'update', 'delete', 'manage_access'],
      requires: 'targetUserOrgId',
      error: 'targetUserOrgId required',
    },
  ],
  billing: [
    {
      actions: [
        'read',
        'list',
        'manage_billing',
        'update',
        'add_credits',
        'edit_budget',
        'manage_stripe',
      ],
      requires: 'orgId',
      error: 'orgId required',
    },
  ],
  script: [
    {
      actions: ['read', 'list', 'create', 'update', 'delete'],
      requires: 'orgId',
      error: 'orgId required',
    },
  ],
  // `ask` is org-scoped like `record.list`: the question may span a record,
  // a CRM entity or the whole organization, so there is no single projectId
  // to demand — requiring one would 403 everybody, the platform owner
  // included (validation runs before the owner short-circuit, see
  // `analysis.list` above). orgId IS required: `denyIfOrgMismatch` is inert
  // when orgId is omitted, and without this rule a caller that forgot it
  // would leave `requiresOrgMatch` doing nothing and hand every role an
  // unbounded allow (audit H7 pattern, #765 §5.1).
  assistant: [{ actions: ['ask'], requires: 'orgId', error: 'orgId required' }],
  // Org-wide by nature (every project's deliveries, every vendor's health):
  // without orgId `requiresOrgMatch` would be inert and the rule an unbounded
  // allow for client/admin of ANY organization.
  integration_operations: [
    { actions: ['manage'], requires: 'orgId', error: 'orgId required' },
  ],
  // `list` is org-scoped like `record.list` (the queue spans every project, so
  // there is no single projectId; orgId keeps `requiresOrgMatch` from going
  // inert). `task` acts on one deal, whose project the route takes from the
  // `deal_states` row — `projectAccessFallback` needs it, like `record.rate`.
  deal: [
    { actions: ['list'], requires: 'orgId', error: 'orgId required' },
    { actions: ['task'], requires: 'projectId', error: 'projectId required' },
  ],
};

/**
 * Is the resource description itself incomplete for this action (a missing
 * `id`/`orgId`/`projectId`)? `null` — it is complete.
 *
 * `checkPermission` answers `false` for such a call exactly as it does for a
 * refused role, even to the platform owner (validation runs before the owner
 * short-circuit). This is how a caller telling the two apart — the logging
 * wrapper in edge-api — finds out which one it was: a route that forgot the
 * `id` is a bug in the route, not a decision about the user.
 */
export function validateResourceContext(
  resource: ResourceContext,
  action: Action
): PermissionCheckResult | null {
  const rules = validationRules[resource.type];
  if (!rules) return null;

  for (const rule of rules) {
    if (!rule.actions.includes(action)) continue;

    const field = rule.requires;
    // Special case: user operations can use orgId OR targetUserOrgId
    if (field === 'targetUserOrgId' && (resource.orgId || resource.targetUserOrgId))
      continue;

    const value = resource[field as keyof ResourceContext];
    if (!value) {
      return denyInvalid(rule.error);
    }
  }

  // Special validation for user role changes
  if (
    resource.type === 'user' &&
    action === 'update' &&
    resource.newRole !== undefined &&
    !resource.newRole
  ) {
    return denyInvalid('newRole cannot be empty for role updates');
  }

  return null;
}

// ============================================================================
// Declarative Permission Matrix
// ============================================================================

/**
 * PERMISSION_MATRIX — single source of truth for all RBAC rules.
 *
 * Each entry maps: resource type → action → PermissionRule.
 * Missing actions are denied by default ("Unknown action").
 *
 * Evaluation order per rule (in checkResource):
 * 1. ownerOnly check
 * 2. specialCondition (custom logic)
 * 3. requiresOrgMatch (org boundary)
 * 4. roles (direct global role match)
 * 5. projectScopedRoles (global role + project access)
 * 6. projectAccessFallback (any project membership)
 * 7. deny with denyReason
 */
const PERMISSION_MATRIX: PermissionMatrix = {
  // ── Project ────────────────────────────────────────────────────────────
  project: {
    create: {
      // Supervisors included by product decision (2026-07-28): they already
      // see the projects screen (SCREEN_ACCESS.projects), so denying the
      // create they were being offered produced 403s in production. Editing
      // stays narrower — the creator is auto-assigned project 'admin', which
      // is what grants them update/archive on what they made.
      roles: ['client', 'admin', 'supervisor'],
      requiresOrgMatch: true,
      denyReason: 'Insufficient role for project creation',
    },
    read: {
      roles: ['client', 'admin'],
      requiresOrgMatch: true,
      projectAccessFallback: true,
      denyReason: 'Project not assigned',
    },
    list: {
      roles: ['client', 'admin', 'agent', 'supervisor'],
      denyReason: 'Insufficient permissions',
    },
    update: {
      roles: ['client', 'admin'],
      requiresOrgMatch: true,
      projectAccessFallback: 'admin',
      denyReason: 'Insufficient permissions',
    },
    archive: {
      roles: ['client', 'admin'],
      requiresOrgMatch: true,
      projectAccessFallback: 'admin',
      denyReason: 'Insufficient permissions',
    },
    delete: {
      roles: ['client'],
      requiresOrgMatch: true,
      denyReason: 'Only client can delete projects',
    },
    upload: {
      roles: ['client', 'admin', 'agent'],
      requiresOrgMatch: true,
      denyReason: 'Insufficient permissions to upload',
    },
    // `projectAccessFallback: 'admin'` mirrors `update`/`archive`: whoever
    // administers a project decides who oversees it. Without it the settings
    // screen — which admits anyone passing `project.update` — showed the Team
    // tab to a supervisor who created the project, and the API answered 403.
    // That is the same UI-offers-what-the-API-refuses defect the project.create
    // rule above was widened to fix, on a new surface.
    manage_users: {
      roles: ['client', 'admin'],
      requiresOrgMatch: true,
      projectAccessFallback: 'admin',
      denyReason: 'Insufficient permissions',
    },
  },

  // ── Record ─────────────────────────────────────────────────────────────
  record: {
    create: {
      roles: ['client', 'admin', 'agent'],
      denyReason: 'Insufficient permissions',
    },
    upload: {
      roles: ['client', 'admin', 'agent'],
      denyReason: 'Insufficient permissions',
    },
    // requiresOrgMatch: when a handler resolves the record's true org
    // (record.projectId → project.orgId) and passes it in, the org boundary is
    // enforced centrally — closing the cross-org IDOR class instead of relying
    // on every handler to remember isAccessibleByOrg (audit H7). Inert (no deny)
    // when orgId is omitted or equals auth.orgId, so it never breaks org-wide
    // role access; the route-level isAccessibleByOrg remains the data boundary.
    // `agent` is a DIRECT role here, on purpose, and it is not a widening:
    // an agent's boundary is the record they handled, which this matrix cannot
    // express — it never sees `record.agentId`. Scoping them by project instead
    // was a stand-in that fit badly and failed in both directions: it let an
    // assigned agent read a colleague's call, and it left an unassigned agent
    // unable to open their own.
    //
    // The real boundary is `agentOwnsRecord` (`@aica/database`,
    // `access/agent-record-ownership`). Callers, ACROSS BOTH APIS — an earlier
    // version of this note said "every caller" while listing only edge-api, and
    // a review found the workflows-api one unguarded:
    //   edge-api  records `GET /:id`, `/:id/audio`, `/:id/transcript`,
    //             `/:id/script-adherence`; `search.ts` (pushed into the query)
    //   workflows-api  `routes/records.ts` `GET /:recordId`
    // Adding a caller without that guard hands agents the whole org.
    read: {
      roles: ['client', 'admin', 'agent'],
      requiresOrgMatch: true,
      projectAccessFallback: true,
      specialCondition: hierarchyAllows,
      denyReason: 'Record not accessible',
    },
    // A COLLECTION, so this rule answers only "may this role list records at
    // all" — org-wide vs narrowed is not expressible here and is not decided
    // here. The row boundary is put into the query by each caller:
    // - `records/core.ts` (the list) — `buildViewerScopeCondition`
    //   (@aica/database): projects ∪ own records ∪ hierarchy;
    // - `dashboard-quality.ts` (the quality slice, #1235) — the list's own
    //   pieces, narrower on purpose: `d1RecordScopeCondition` for a
    //   supervisor, `agentOwnRecordsCondition` alone for an agent (a
    //   department head's score is theirs, not their team's).
    // A change to "which rows are whose" goes through both. A supervisor/
    // agent with an empty boundary gets an empty page, not a 403.
    //
    // `projectScopedRoles` used to carry supervisor/agent, alongside
    // `hierarchyAllows` as a specialCondition. Both were inoperative theatre:
    // `projectScopedRoles` resolves through `hasProjectAccess(projectId || id)`
    // and a list call has neither, so it never once granted; the caller then
    // compensated by passing `visibleViaHierarchy: !!viewerScope`, which is
    // true for every non-org-wide role and so made the gate decide nothing —
    // while overloading a field documented as "this record is visible" with a
    // second meaning, "this caller has a scope". Behaviour is unchanged by
    // listing the roles outright; what changes is that the rule now says what
    // it does.
    list: {
      roles: ['client', 'admin', 'supervisor', 'agent'],
      requiresOrgMatch: true,
      denyReason: 'Record not accessible',
    },
    // Rating the AI's analysis, split out of `update` (2026-07-29).
    //
    // `update` gates archive, re-analyze, retry-media, retry-transcription and
    // delivery retry — operations that spend money or destroy state. Rating is
    // none of those: it is feedback on a result you are already allowed to see,
    // and it is the one write an agent is meant to perform. Sharing a permission
    // with the destructive five meant agents could only be admitted to rating by
    // also being admitted to those, so they were admitted to neither.
    //
    // Scoping mirrors `read` exactly — you may rate what you may read:
    // client/admin org-wide, supervisor through project membership, agent
    // outright with `agentOwnsRecord` narrowing them to their own record at the
    // route (`records/core.ts` PATCH /:id/agent-rating).
    rate: {
      roles: ['client', 'admin', 'agent'],
      requiresOrgMatch: true,
      projectAccessFallback: true,
      specialCondition: hierarchyAllows,
      denyReason: 'Record not accessible',
    },
    // Archive, re-analyze, retry-media, retry-transcription, retry-delivery —
    // paid or destructive, and NONE of those routes checks record ownership.
    // `agent` was removed from the scoped roles (2026-07-29): membership was the
    // only thing keeping an assigned agent off a colleague's record there, and
    // an agent has no business archiving or re-billing anyone's call, their own
    // included. Rating moved to `rate`, so nothing agent-facing needs `update`.
    update: {
      roles: ['client', 'admin'],
      requiresOrgMatch: true,
      projectScopedRoles: ['supervisor'],
      denyReason: 'Record not accessible',
    },
    delete: {
      roles: ['client', 'admin'],
      requiresOrgMatch: true,
      denyReason: 'Insufficient permissions',
    },
  },

  // ── Analysis ───────────────────────────────────────────────────────────
  // requiresOrgMatch mirrors record.*: analysis org resolves via
  // analysis → record → project → org; enforced centrally when supplied (H7).
  analysis: {
    // Same reasoning as `record.read`: the agent is bounded by the record they
    // handled, not by project membership. Callers that must apply
    // `agentOwnsRecord`: `analyses/core.ts` (GET /:id), and
    // `analyses/annotations.ts` + `analyses/reviews.ts` on their GETs.
    //
    // Their WRITE halves deliberately do not use this rule: re-analyze moved to
    // `record.update` (it spends money), review-save to `human-review.update`
    // (it can overwrite a supervisor's verdict), and incidents to
    // `human-review` outright. Widening a read rule must never be a way to
    // reach a write.
    read: {
      roles: ['client', 'admin', 'agent'],
      requiresOrgMatch: true,
      projectScopedRoles: ['supervisor'],
      specialCondition: hierarchyAllows,
      denyReason: 'Analysis not accessible',
    },
    list: {
      roles: ['client', 'admin'],
      requiresOrgMatch: true,
      projectScopedRoles: ['supervisor', 'agent'],
      denyReason: 'Analysis not accessible',
    },
  },

  // ── Alert ──────────────────────────────────────────────────────────────
  alert: {
    read: {
      roles: ['client', 'admin'],
      projectScopedRoles: ['supervisor'],
      denyReason: 'Alert not accessible',
    },
    list: {
      roles: ['client', 'admin'],
      projectScopedRoles: ['supervisor'],
      denyReason: 'Alert not accessible',
    },
  },

  // ── Organization ───────────────────────────────────────────────────────
  organization: {
    read: {
      roles: [],
      specialCondition: (_auth, _action, r) =>
        r.orgId && r.orgId === _auth.orgId
          ? allow()
          : denyScope('Cannot access other organizations'),
      denyReason: 'Cannot access other organizations',
    },
    list: {
      roles: [],
      specialCondition: (_auth, _action, r) =>
        r.orgId && r.orgId === _auth.orgId
          ? allow()
          : denyScope('Cannot access other organizations'),
      denyReason: 'Cannot access other organizations',
    },
    update: {
      roles: ['client', 'admin'],
      requiresOrgMatch: true,
      denyReason: 'Insufficient permissions',
    },
    delete: {
      ownerOnly: true,
      roles: [],
      denyReason: 'Only platform owner can delete organizations',
    },
    manage_settings: {
      roles: ['client'],
      requiresOrgMatch: true,
      denyReason: 'Only organization owner can manage settings',
    },
  },

  // ── Script ─────────────────────────────────────────────────────────────
  script: {
    read: {
      roles: ['client', 'admin', 'supervisor'],
      requiresOrgMatch: true,
      denyReason: 'Insufficient permissions',
    },
    list: {
      roles: ['client', 'admin', 'supervisor'],
      requiresOrgMatch: true,
      denyReason: 'Insufficient permissions',
    },
    create: {
      roles: ['client', 'admin'],
      requiresOrgMatch: true,
      denyReason: 'Insufficient permissions',
    },
    update: {
      roles: ['client', 'admin'],
      requiresOrgMatch: true,
      denyReason: 'Insufficient permissions',
    },
    delete: {
      roles: ['client', 'admin'],
      requiresOrgMatch: true,
      denyReason: 'Insufficient permissions',
    },
  },

  // ── User ───────────────────────────────────────────────────────────────
  user: {
    list: {
      roles: ['client', 'admin'],
      requiresOrgMatch: true,
      denyReason: 'Insufficient permissions',
    },
    read: {
      roles: ['client', 'admin'],
      requiresOrgMatch: true,
      denyReason: 'Insufficient permissions',
    },
    create: {
      roles: ['client', 'admin'],
      requiresOrgMatch: true,
      // Inviting someone as a client is assigning the client role (#877): an
      // admin who could invite a second client could then have it demote the
      // first one.
      specialCondition: (auth, _action, r) =>
        r.newRole === 'client' && !isRole(auth, 'client')
          ? deny('Only client can invite a client')
          : null,
      denyReason: 'Insufficient permissions',
    },
    update: {
      roles: ['client', 'admin'],
      requiresOrgMatch: true,
      specialCondition: (auth, _action, r) => {
        // Only a client may change a client (#877): the admin is the junior
        // role and must not take billing access away from the senior one.
        if (r.targetRole === 'client' && !isRole(auth, 'client')) {
          return deny('Only client can change a client');
        }
        // Role changes: client and admin can change roles
        // Only client can assign the 'client' role
        if (r.newRole) {
          if (!isRole(auth, 'client') && !isRole(auth, 'admin')) {
            return deny('Only client or admin can change user roles');
          }
          if (r.newRole === 'client' && !isRole(auth, 'client')) {
            return deny('Only client can assign the client role');
          }
        }
        return null;
      },
      denyReason: 'Insufficient permissions',
    },
    delete: {
      roles: ['client', 'admin'],
      requiresOrgMatch: true,
      specialCondition: (auth, _action, r) =>
        // Same hierarchy as update (#877): only a client removes a client.
        r.targetRole === 'client' && !isRole(auth, 'client')
          ? deny('Only client can remove a client')
          : null,
      denyReason: 'Insufficient permissions',
    },
    // Assign or revoke a narrow analytics grant (E1.5 PR 5, #1336; spec
    // «Решения» 4: client and admin of their own org, the platform owner as
    // always). Narrowing a client is demoting one, so — as for the role
    // (#877) — only a client may touch a client. `targetRole` is REQUIRED
    // here: a route that did not look the target up cannot protect a client,
    // so its absence is a denial, not a pass. Self, membership, «a grant only
    // on a supervisor or an agent» (owner decision 05.10 — this action also
    // covers REVOKING, which any target may need) and a refused `managed`
    // assigner are the route's (`routes/users/access.ts`).
    manage_access: {
      roles: [...MANAGE_ACCESS_ROLES],
      requiresOrgMatch: true,
      specialCondition: (auth, _action, r) => {
        if (!r.targetRole) return denyInvalid('Target role is required to manage access');
        return hasRole(auth, [...rolesManagingAccessOf(r.targetRole)])
          ? null
          : deny('Only client can manage the access of a client');
      },
      denyReason: 'Only client or admin can manage access',
    },
  },

  // ── Billing ────────────────────────────────────────────────────────────
  billing: {
    read: {
      roles: ['client'], // Only client (business owner) can access billing
      requiresOrgMatch: true,
      denyReason: 'Only client can access billing information',
    },
    list: {
      roles: ['client'], // Only client (business owner) can access billing
      requiresOrgMatch: true,
      denyReason: 'Only client can access billing information',
    },
    manage_billing: {
      roles: ['client'],
      requiresOrgMatch: true,
      denyReason: 'Only organization owner can manage billing',
    },
    update: {
      roles: ['client'],
      requiresOrgMatch: true,
      denyReason: 'Only organization owner can manage billing',
    },
    add_credits: {
      ownerOnly: true,
      roles: [],
      denyReason: 'Only platform owner can add credits via Admin Panel',
    },
    edit_budget: {
      roles: ['client'],
      requiresOrgMatch: true,
      denyReason: 'Only organization owner can edit budget',
    },
    manage_stripe: {
      roles: ['client'],
      requiresOrgMatch: true,
      denyReason: 'Only organization owner can manage Stripe subscription',
    },
  },

  // ── Agent ──────────────────────────────────────────────────────────────
  agent: {
    read: {
      roles: ['client', 'admin', 'supervisor'],
      specialCondition: (auth, _action, r) =>
        r.agentId === auth.userId ? allow() : null,
      denyReason: "Only supervisors and admins can view other agents' data",
    },
    list: {
      roles: ['client', 'admin', 'supervisor'],
      denyReason: 'Only supervisors and admins can view team data',
    },
    create: {
      roles: ['client', 'admin', 'supervisor'],
      denyReason: 'Only supervisors and admins can create coaching notes',
    },
    update: {
      roles: ['client', 'admin'],
      denyReason: 'Only admins can modify agent data',
    },
    delete: {
      roles: ['client', 'admin'],
      denyReason: 'Only admins can modify agent data',
    },
  },

  // ── Consent (Compliance) ───────────────────────────────────────────────
  consent: {
    read: {
      roles: ['client', 'admin'],
      requiresOrgMatch: true,
      denyReason: 'Only admins can view consent data',
    },
    list: {
      roles: ['client', 'admin'],
      requiresOrgMatch: true,
      denyReason: 'Only admins can list consent events',
    },
    create: {
      roles: ['client', 'admin'],
      requiresOrgMatch: true,
      denyReason: 'Only admins can manage consent',
    },
    update: {
      roles: ['client', 'admin'],
      requiresOrgMatch: true,
      denyReason: 'Only admins can modify consent settings',
    },
  },

  // ── DSR — Data Subject Requests (Compliance) ──────────────────────────
  dsr: {
    read: {
      roles: ['client', 'admin'],
      requiresOrgMatch: true,
      denyReason: 'Only admins can view DSR requests',
    },
    list: {
      roles: ['client', 'admin'],
      requiresOrgMatch: true,
      denyReason: 'Only admins can list DSR requests',
    },
    create: {
      roles: ['client', 'admin'],
      requiresOrgMatch: true,
      denyReason: 'Only admins can create DSR requests',
    },
    update: {
      roles: ['client', 'admin'],
      requiresOrgMatch: true,
      denyReason: 'Only admins can approve/execute DSR requests',
    },
    delete: {
      roles: ['client'],
      denyReason: 'Only org owner can delete DSR requests',
    },
  },

  // ── Human Review (Compliance) ─────────────────────────────────────────
  'human-review': {
    read: {
      roles: ['client', 'admin', 'supervisor'],
      requiresOrgMatch: true,
      denyReason: 'Only supervisors and admins can view review queue',
    },
    list: {
      roles: ['client', 'admin', 'supervisor'],
      requiresOrgMatch: true,
      denyReason: 'Only supervisors and admins can list reviews',
    },
    update: {
      roles: ['client', 'admin', 'supervisor'],
      requiresOrgMatch: true,
      denyReason: 'Only supervisors and admins can approve/reject reviews',
    },
  },

  // ── API Key ────────────────────────────────────────────────────────────
  // Issuing a key spends org money on ASR/LLM, so it sits at the same
  // privilege level as billing (canSeeBilling: isOwner || client).
  api_key: {
    manage: {
      roles: ['client'],
      requiresOrgMatch: true,
      denyReason: 'Only the organization owner can manage API keys',
    },
  },

  // ── Assistant ──────────────────────────────────────────────────────────
  // Asking the AI assistant about calls (#765, spec §5). Every org role may
  // ask: the assistant only restates records the caller can already read,
  // so the rule decides "may this role ask at all" — a collection-style
  // rule like `record.list`. The row boundary (which records feed the
  // answer) is NOT decided here: it is `viewerScope` in the caller's SQL plus
  // `recordVisibleTo` re-authorization of `source_record_ids` on every read
  // (§5.2). Do not pass `visibleViaHierarchy` for this resource. Public-API
  // callers carry `globalRole: null` and are denied by the matrix itself.
  assistant: {
    ask: {
      roles: ['client', 'admin', 'supervisor', 'agent'],
      requiresOrgMatch: true,
      denyReason: 'Assistant not accessible',
    },
  },

  // ── Deal (#766 PR4a) ────────────────────────────────────────────────────
  // The deal queue and card. Visibility of a deal is NOT decided here: it is
  // the visibility of the conversation the deal's current state is built on
  // (`based_on_record_id`, owner decision 04.10 Q1), enforced by the route
  // with the record boundary (`gateRecordRead` / `buildViewerScopeCondition`).
  deal: {
    // A COLLECTION, like `record.list`: the rule answers only "may this role
    // open the queue at all". The query bounds the rows; a supervisor or agent
    // with nothing visible gets an empty queue, not a 403. No project
    // fallback — a list call has no projectId.
    list: {
      roles: ['client', 'admin', 'supervisor', 'agent'],
      requiresOrgMatch: true,
      denyReason: 'Deals not accessible',
    },
    // The CRM task for the deal's next action (owner decision 04.10 Q5): you
    // act on the deal you can see, so scoping mirrors `record.rate` —
    // client/admin org-wide, supervisor through project membership or
    // `visibleViaHierarchy`, agent outright and narrowed at the route by the
    // visibility of the `based_on_record_id` record (`agentOwnsRecord`).
    // Deliberately not `record.update`: agents do not have it.
    task: {
      roles: ['client', 'admin', 'agent'],
      requiresOrgMatch: true,
      projectAccessFallback: true,
      specialCondition: hierarchyAllows,
      denyReason: 'Deal not accessible',
    },
  },

  // ── Provider cost (#1148) ───────────────────────────────────────────────
  // What the platform pays its providers: the `costs` table, an analysis' or a
  // script evaluation's cost, the internal `usage_debit.*` ledger rows. Not the
  // customer's bill (usage_charges), which stays under `billing.read`.
  provider_cost: {
    read: {
      ownerOnly: true,
      roles: [],
      denyReason: 'Only the platform owner can view provider costs',
    },
  },

  // ── Provider credential (#1954, #2855) ──────────────────────────────────
  // Setting, changing or removing an organization's provider key. The key
  // decides whose account pays for the organization's calls and switches off
  // the built-in criteria shadow (#868, decision 9), so it is the platform
  // owner's, as /admin/api-keys is. Not `organization.manage_settings`: the
  // client holds that on its own organization. Reading the key's metadata is
  // the owner's too (#2855): an organization's own key is not a customer
  // capability, and a customer must not learn from the API that one exists.
  provider_credential: {
    read: {
      ownerOnly: true,
      roles: [],
      denyReason: 'Only the platform owner can view provider credentials',
    },
    manage: {
      ownerOnly: true,
      roles: [],
      denyReason: 'Only the platform owner can change provider credentials',
    },
  },

  // ── Integration operations (#1548) ──────────────────────────────────────
  // The organization's delivery plumbing: every failed delivery (record ids,
  // projects, targets, the target's error text), the CRM/email integration
  // health, and the retry that restarts the paid analyze→deliver pipeline.
  // ONE rule for the reads and the action: whoever may see a failure may
  // retry it, and nobody else may do either. Its own resource, not a reused
  // `organization.update` — the role set matches today, but the two
  // decisions are unrelated and must not drift together (see
  // `canManageOrgStructure`). Project-scoped roles are not admitted: the
  // surface is org-wide and has no per-project narrowing for a supervisor.
  integration_operations: {
    manage: {
      roles: ['client', 'admin'],
      requiresOrgMatch: true,
      denyReason: 'Only organization owners and admins can operate integrations',
    },
  },
};

// ============================================================================
// Universal Resource Checker
// ============================================================================

/**
 * Evaluate a PermissionRule against auth context and resource.
 *
 * Follows strict evaluation order:
 * 1. ownerOnly → deny (owner already handled before this function)
 * 2. specialCondition → return result if non-null
 * 3. requiresOrgMatch → deny if org mismatch
 * 4. roles → allow if direct role match
 * 5. projectScopedRoles → allow if role + project access
 * 6. projectAccessFallback → allow if project membership (optionally with specific role)
 * 7. deny with reason
 */
function checkResource(
  auth: AuthContext,
  action: Action,
  resource: ResourceContext
): PermissionCheckResult {
  const resourceRules = PERMISSION_MATRIX[resource.type];
  if (!resourceRules) {
    return denyInvalid(`Unknown resource type: ${resource.type}`);
  }

  const rule: PermissionRule | undefined = resourceRules[action];
  if (!rule) {
    return denyInvalid(`Unknown action: ${action}`);
  }

  // 1. Owner-only actions (platform owner already checked in checkPermission/checkPermissionDetailed)
  if (rule.ownerOnly) {
    return deny(rule.denyReason ?? 'Only platform owner can perform this action');
  }

  // 2. Special condition (custom logic runs first)
  if (rule.specialCondition) {
    const result = rule.specialCondition(auth, action, resource);
    if (result !== null) return result;
  }

  // 3. Org match check
  if (rule.requiresOrgMatch) {
    // For user resources, use targetUserOrgId OR orgId
    const orgIdToCheck =
      resource.type === 'user'
        ? resource.targetUserOrgId || resource.orgId
        : resource.orgId;
    const mismatch = denyIfOrgMismatch(auth, orgIdToCheck);
    if (mismatch) return mismatch;
  }

  // 4. Direct role match
  if (rule.roles.length > 0 && hasRole(auth, rule.roles)) {
    return allow();
  }

  // 5. Project-scoped roles (role + project access)
  if (rule.projectScopedRoles) {
    for (const scopedRole of rule.projectScopedRoles) {
      if (
        isRole(auth, scopedRole) &&
        hasProjectAccess(auth, resource.projectId || resource.id)
      ) {
        return allow();
      }
    }
  }

  // 6. Project access fallback
  if (rule.projectAccessFallback !== undefined) {
    const requiredProjectRole =
      rule.projectAccessFallback === true ? undefined : rule.projectAccessFallback;
    if (hasProjectAccess(auth, resource.projectId || resource.id, requiredProjectRole)) {
      return allow();
    }
  }

  // 7. Deny
  return deny(rule.denyReason ?? 'Insufficient permissions');
}

// ============================================================================
// Main Permission Checker
// ============================================================================

/**
 * Centralized permission checker (PURE FUNCTION)
 *
 * @param auth - Authentication context
 * @param action - Action to perform
 * @param resource - Resource context
 * @returns boolean - true if allowed
 */
export function checkPermission(
  auth: AuthContext,
  action: Action,
  resource: ResourceContext
): boolean {
  if (!action) throw new Error('Action is required');
  if (!auth) throw new Error('Authentication context is required');
  if (!resource) throw new Error('Resource context is required');

  const validation = validateResourceContext(resource, action);
  if (validation) return false;

  if (auth.isOwner) return true;

  return checkPermissionDetailed(auth, action, resource).allowed;
}

/**
 * Detailed permission check with reason
 */
export function checkPermissionDetailed(
  auth: AuthContext,
  action: Action,
  resource: ResourceContext
): PermissionCheckResult {
  if (auth.isOwner) return allow();

  const validation = validateResourceContext(resource, action);
  if (validation) return validation;

  // Organization requires orgId for all actions (special validation)
  if (resource.type === 'organization' && !resource.orgId) {
    return denyInvalid('Organization ID required');
  }

  return checkResource(auth, action, resource);
}

// ============================================================================
// Frontend Helper Functions
// ============================================================================

export function canManageProjects(
  globalRole: GlobalRole | null,
  isOwner = false
): boolean {
  if (isOwner) return true;
  return globalRole !== null && ['client', 'admin'].includes(globalRole);
}

/**
 * May the role create a project? Mirrors PERMISSION_MATRIX.project.create.
 *
 * Deliberately separate from `canManageProjects`, which is also the gate for
 * the billing-flavoured banners (PauseOffer, TierAdvice, DataSource). Widening
 * that helper to supervisors would have shown them tier advice — a different
 * decision, and one `canSeeBilling` (owner/client) contradicts.
 */
export function canCreateProject(
  globalRole: GlobalRole | null,
  isOwner = false
): boolean {
  if (isOwner) return true;
  return globalRole !== null && ['client', 'admin', 'supervisor'].includes(globalRole);
}

export function canSeeBilling(globalRole: GlobalRole | null, isOwner = false): boolean {
  return isOwner || globalRole === 'client';
}

/**
 * May the caller see provider cost (COGS)? Mirrors
 * `PERMISSION_MATRIX.provider_cost.read` (owner only) — a contract test holds
 * the two together.
 *
 * For shaping a response that also carries other data (dashboard, analysis
 * detail, …): it records nothing, so an ordinary client read does not leave an
 * RBAC denial per request. A route whose whole purpose is provider cost checks
 * `provider_cost` through the edge-api `checkPermission` wrapper instead, so a
 * refused attempt is logged.
 */
export function canViewProviderCosts(isOwner: boolean): boolean {
  return isOwner;
}

/**
 * May the role manage org structure (import departments/heads from the CRM,
 * assign a department head)? Mirrors `PERMISSION_MATRIX.organization.manage_settings`,
 * which restricts this to the org owner (`client`) plus the platform owner —
 * `admin` does NOT qualify, unlike `canManageUsers`.
 *
 * Named separately from `canSeeBilling` even though the formula is identical
 * today: the two gate unrelated permissions (billing vs. org-structure
 * `manage_settings`) that only happen to share a role set right now. Reusing
 * `canSeeBilling` for this would silently drift the day either permission's
 * role set changes on its own — e.g. if admins are ever given billing access
 * without also being granted org-structure management.
 */
export function canManageOrgStructure(
  globalRole: GlobalRole | null,
  isOwner = false
): boolean {
  return isOwner || globalRole === 'client';
}

/**
 * May the role edit the organization's theme set — add, rename, change a
 * definition, archive, unarchive (#845, spec 2026-10-02-conversation-themes
 * §2 Р5)? Mirrors `PERMISSION_MATRIX.organization.update`, the action the
 * edge-api routes check: `client`, `admin` and the platform owner. The set is
 * an analytics setting, not billing or access, so `admin` qualifies — unlike
 * `canManageOrgStructure` (`manage_settings`). Everyone who sees Voice of
 * Customer reads the set; this gates only the edit controls.
 */
export function canEditThemeSet(globalRole: GlobalRole | null, isOwner = false): boolean {
  if (isOwner) return true;
  const rule = PERMISSION_MATRIX.organization.update;
  return globalRole !== null && rule !== undefined && rule.roles.includes(globalRole);
}

/**
 * Does the role see the WHOLE organization — every project, no membership
 * narrowing? `client`, `admin` and the platform owner. The server narrows
 * project filters by it (`narrowProjectFilter`) and gates org-wide aggregates
 * on it (criteria activity, #861); the UI asks the same question before
 * requesting such an aggregate, instead of collecting an expected 403.
 */
export function canViewWholeOrg(globalRole: GlobalRole | null, isOwner = false): boolean {
  return isOwner || globalRole === 'client' || globalRole === 'admin';
}

export function canManageUsers(globalRole: GlobalRole | null, isOwner = false): boolean {
  if (isOwner) return true;
  return globalRole !== null && ['client', 'admin'].includes(globalRole);
}

/**
 * May the role read the org's user list (GET /users)? Mirrors
 * `PERMISSION_MATRIX.user.list`. Separate from `canManageUsers` though the
 * formula matches today: a screen that only needs names (the upload modal's
 * agent picker) must not widen or narrow with who may invite and remove.
 */
export function canListUsers(globalRole: GlobalRole | null, isOwner = false): boolean {
  if (isOwner) return true;
  return globalRole !== null && ['client', 'admin'].includes(globalRole);
}

/**
 * Can the user connect/test/disconnect a telephony data source for their org?
 *
 * This is an org-level integration action, so it mirrors project/user management:
 * the org owner (`client`) and org admins, plus the platform owner. IMPORTANT:
 * `client` IS the org owner (see onboarding: "role: client = owner") — the gate
 * that previously required `admin`/isOwner locked every real customer out of
 * their own org's source setup with a bare 403 Forbidden.
 */
export function canManageSources(
  globalRole: GlobalRole | null,
  isOwner = false
): boolean {
  if (isOwner) return true;
  return globalRole !== null && ['client', 'admin'].includes(globalRole);
}

export function canUploadRecords(
  globalRole: GlobalRole | null,
  isOwner = false
): boolean {
  if (isOwner) return true;
  return globalRole !== null && ['client', 'admin', 'agent'].includes(globalRole);
}

/**
 * The roles `PERMISSION_MATRIX.agent.create` admits — the check both coaching
 * write routes make. Read from the matrix, not restated: the rule is a plain
 * role list (no project scope, no special condition), so its `roles` are the
 * whole answer for a caller who is not the platform owner.
 */
const COACHING_WRITE_ROLES: readonly GlobalRole[] =
  PERMISSION_MATRIX.agent.create?.roles ?? [];

/**
 * May the role write coaching — a note, a recognition, a session (POST
 * `/agents/coaching/notes`, `/agents/coaching/sessions`)? The roles of
 * `PERMISSION_MATRIX.agent.create`, plus the platform owner.
 *
 * Asked by the ROLE alone on purpose: an E1.5 grant delegates reading, and a
 * write is role ∧ grant (#1336), so a group lead whose base role is `agent`
 * reads its people's coaching but writes none of it (#1645).
 */
export function canWriteCoaching(
  globalRole: GlobalRole | null,
  isOwner = false
): boolean {
  if (isOwner) return true;
  return globalRole !== null && COACHING_WRITE_ROLES.includes(globalRole);
}

/**
 * The roles `PERMISSION_MATRIX.integration_operations.manage` admits — the check
 * behind the failed deliveries, the integration health, the delivery retry and
 * the inbound activity log (`GET /ingest-log`, #1624). Read from the matrix, not
 * restated: the rule is a plain role list (no project scope, no special
 * condition), so its `roles` are the whole answer for a caller who is not the
 * platform owner. The org match the server also makes is the caller's own
 * organization here — a viewer only ever asks about the one they are in.
 */
const INTEGRATION_OPERATIONS_ROLES: readonly GlobalRole[] =
  PERMISSION_MATRIX.integration_operations?.manage?.roles ?? [];

/**
 * May the viewer open the organization's integration-operations surface —
 * client, admin and the platform owner; never a supervisor or an agent? The
 * server stays the authority; this only stops a page offering a tab whose every
 * read is a 403.
 */
export function canOperateIntegrations(
  globalRole: GlobalRole | null,
  isOwner = false
): boolean {
  if (isOwner) return true;
  return globalRole !== null && INTEGRATION_OPERATIONS_ROLES.includes(globalRole);
}

export function canManageAlerts(globalRole: GlobalRole | null, isOwner = false): boolean {
  if (isOwner) return true;
  // `client` is the org owner — by design "admin = client minus billing", so the
  // owner must have every capability admin has. Supervisors also handle alerts.
  return globalRole !== null && ['client', 'admin', 'supervisor'].includes(globalRole);
}

/**
 * May this caller change the organization's data-retention window?
 *
 * Narrower than every other org setting on purpose: a lowered value permanently
 * deletes historical records, so `admin` — who may edit all the rest — is
 * excluded, leaving the org owner (`client`) and the platform owner.
 *
 * Shared rather than restated because the UI restated it once and got it wrong:
 * `PATCH /settings` authorizes `isOwner || globalRole === 'client'`, while the
 * screen gated the control on `users.is_owner` alone — the platform
 * super-admin flag that no signup or onboarding path ever sets. The result was
 * a headline retention feature, promised in the DPA and the Privacy Policy,
 * that no Controller could reach while Aelo staff could.
 */
export function canManageDataRetention(
  globalRole: GlobalRole | null,
  isOwner = false
): boolean {
  if (isOwner) return true;
  return globalRole === 'client';
}

/**
 * Does this role need project membership to be shown an alert?
 *
 * Derived from the matrix entry for `alert.read` rather than restated, so the
 * answer cannot drift from what the API enforces: `roles` see every alert in
 * the org, `projectScopedRoles` only alerts from projects they belong to, and
 * anything absent from both is not an alert audience at all.
 *
 * Delivery needs this because the API gate never runs on that path. Nobody
 * requests a notification — it is pushed — so an org-wide recipient query is
 * the one place where a supervisor can be handed alert contents from a project
 * they cannot open in the UI.
 *
 * @returns `'org-wide'` — every alert in the org · `'project-scoped'` — only
 *   assigned projects · `'none'` — never an alert recipient
 */
export function alertAudienceScope(
  globalRole: GlobalRole | null
): 'org-wide' | 'project-scoped' | 'none' {
  if (globalRole === null) return 'none';

  const rule = PERMISSION_MATRIX.alert?.read;
  if (!rule) return 'none';

  if (rule.roles.includes(globalRole)) return 'org-wide';
  if (rule.projectScopedRoles?.includes(globalRole)) return 'project-scoped';
  return 'none';
}

export function canEditProject(
  auth: AuthContext | null,
  projectId: string,
  projectOrgId: string
): boolean {
  return auth
    ? checkPermission(auth, 'update', {
        type: 'project',
        id: projectId,
        orgId: projectOrgId,
      })
    : false;
}

export function canDeleteProject(
  auth: AuthContext | null,
  projectId: string,
  projectOrgId: string
): boolean {
  return auth
    ? checkPermission(auth, 'delete', {
        type: 'project',
        id: projectId,
        orgId: projectOrgId,
      })
    : false;
}

export function canViewProject(auth: AuthContext | null, projectId: string): boolean {
  return auth ? checkPermission(auth, 'read', { type: 'project', id: projectId }) : false;
}

export function canAccessAdminPanel(isOwner: boolean): boolean {
  return isOwner;
}
